import React from 'react';
import { Camera, Heart, Sparkles, ArrowRight, MapPin, Star, Users } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { statistics } from '../data/mock';

const HeroSection = () => {
  const { themeData } = useTheme();
  
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg')",
        }}
      >
        <div 
          className="absolute inset-0"
          style={{ background: themeData.heroGradient }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/60 via-transparent to-[#1a1a2e]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left">
            {/* Valentine Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium mb-6"
              style={{ backgroundColor: `${themeData.colors.primary}33`, border: `1px solid ${themeData.colors.primary}` }}
            >
              <Heart className="w-4 h-4" style={{ color: themeData.colors.primary }} />
              Valentine's Special - Up to 40% Off
              <Sparkles className="w-4 h-4" style={{ color: themeData.colors.accent }} />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Capture Your
              <span 
                className="block bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})` }}
              >
                Love Story
              </span>
              Caribbean Style
            </h1>

            <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto lg:mx-0">
              Professional photography & custom photo products. From passport photos to canvas prints, 
              we capture your special moments with Caribbean warmth.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                size="lg" 
                className="text-white font-semibold px-8 shadow-lg"
                style={{ backgroundColor: themeData.colors.primary }}
              >
                <Camera className="w-5 h-5 mr-2" />
                Book a Session
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
              >
                Browse Products
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/10">
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                  <MapPin className="w-5 h-5" style={{ color: themeData.colors.primary }} />
                  <span className="text-3xl font-bold text-white">{statistics.locations}</span>
                </div>
                <p className="text-white/60 text-sm">Store Locations</p>
              </div>
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                  <Star className="w-5 h-5" style={{ color: themeData.colors.secondary }} />
                  <span className="text-3xl font-bold text-white">{statistics.products}</span>
                </div>
                <p className="text-white/60 text-sm">Custom Products</p>
              </div>
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                  <Users className="w-5 h-5" style={{ color: themeData.colors.accent }} />
                  <span className="text-3xl font-bold text-white">{statistics.customers}</span>
                </div>
                <p className="text-white/60 text-sm">Happy Customers</p>
              </div>
            </div>
          </div>

          {/* Right Column - Featured Card */}
          <div className="hidden lg:block">
            <div 
              className="relative rounded-3xl p-8 backdrop-blur-md border border-white/10"
              style={{ background: themeData.colors.cardBg }}
            >
              <div 
                className="absolute -top-4 -right-4 px-4 py-2 rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: themeData.colors.primary }}
              >
                40% OFF
              </div>
              
              <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-6">
                <img 
                  src="https://images.pexels.com/photos/3014856/pexels-photo-3014856.jpeg"
                  alt="Valentine's Package"
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="text-2xl font-bold text-white mb-2">Valentine's Day Package</h3>
              <p className="text-white/60 mb-4">Professional couple session with 30 edited photos</p>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white/40 line-through text-lg">$499</span>
                  <span 
                    className="text-3xl font-bold ml-2"
                    style={{ color: themeData.colors.secondary }}
                  >
                    $299
                  </span>
                </div>
                <Button 
                  className="text-white"
                  style={{ backgroundColor: themeData.colors.secondary }}
                >
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs">Scroll to explore</span>
        <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
          <div 
            className="w-1.5 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: themeData.colors.primary }}
          />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
